import axios from 'axios';
import { getAuthStorage, setAuthStorage, removeAuthStorage } from './storage';

const baseURL = process.env.REACT_APP_API_URL || '';

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Circuit Breaker: Contador de tentativas de refresh
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let isRefreshing = false;

// Detecta erro de RBAC (permissão negada)
const isRBACError = (error: any): boolean => {
  const msg = error?.response?.data?.msg || error?.response?.data?.message || '';
  return (
    msg.toLowerCase().includes('permissão') ||
    msg.toLowerCase().includes('permission') ||
    msg.toLowerCase().includes('tutores não podem') ||
    msg.toLowerCase().includes('não possui permissão')
  );
};

// Mostra modal de erro ao usuário
const showPermissionError = (message: string) => {
  // Criar modal dinâmico
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  `;

  modal.innerHTML = `
    <div style="
      background: white;
      padding: 30px;
      border-radius: 8px;
      max-width: 500px;
      text-align: center;
    ">
      <h2 style="color: #e74c3c; margin-bottom: 20px;">
        Erro de Permissão
      </h2>
      <p style="margin-bottom: 20px; color: #333;">
        ${message}
      </p>
      <button onclick="window.location.href='/login'" style="
        background: #3498db;
        color: white;
        border: none;
        padding: 10px 30px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 16px;
      ">
        Fazer Login Novamente
      </button>
    </div>
  `;

  document.body.appendChild(modal);
};

// Força logout
const forceLogout = async (reason: string) => {
  await removeAuthStorage();
  refreshAttempts = 0;
  isRefreshing = false;

  showPermissionError(`Sua sessão foi encerrada: ${reason}. Por favor, faça login novamente.`);

  // Redireciona após 2 segundos
  setTimeout(() => {
    window.location.href = '/login';
  }, 2000);
};

api.interceptors.response.use(
  (response) => {
    // Reset contador em caso de sucesso
    refreshAttempts = 0;
    return response;
  },
  // eslint-disable-next-line @typescript-eslint/no-shadow
  async (error) => {
    // Se não houver response, não é erro da API
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status } = error.response;

    // Erro 401 - Não autorizado
    if (status === 401) {
      // URLs que não devem tentar refresh
      const isAuthEndpoint =
        error.config.url.includes('auth') || error.config.url.includes('recover');

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // Verifica se é erro de RBAC (permissão)
      if (isRBACError(error)) {
        const errorMsg =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          'Você não possui permissão para realizar esta ação';

        // Mostra modal de erro sem fazer logout
        const modal = document.createElement('div');
        modal.style.cssText = `
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.7); display: flex;
          align-items: center; justify-content: center; z-index: 9999;
        `;
        modal.innerHTML = `
          <div style="background: white; padding: 30px; border-radius: 8px; max-width: 500px; text-align: center;">
            <h2 style="color: #e74c3c; margin-bottom: 20px;">⛔ Acesso Negado</h2>
            <p style="margin-bottom: 20px; color: #333;">${errorMsg}</p>
            <button id="closePermissionModal" style="
              background: #3498db; color: white; border: none;
              padding: 10px 30px; border-radius: 5px; cursor: pointer; font-size: 16px;
            ">Entendi</button>
          </div>
        `;
        document.body.appendChild(modal);
        document.getElementById('closePermissionModal')?.addEventListener('click', () => {
          document.body.removeChild(modal);
          window.history.back();
        });

        return Promise.reject(error);
      }

      // Circuit Breaker: Verifica tentativas
      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        await forceLogout('Falha ao renovar token após múltiplas tentativas');
        return Promise.reject(error);
      }

      // Evita múltiplos refreshes simultâneos
      if (isRefreshing) {
        return Promise.reject(error);
      }

      try {
        isRefreshing = true;
        refreshAttempts += 1;

        // eslint-disable-next-line no-use-before-define
        const token = await refreshToken();

        if (!token || typeof token !== 'string') {
          throw new Error('Token inválido recebido do refresh');
        }

        // Token renovado com sucesso
        refreshAttempts = 0;
        isRefreshing = false;

        // Retry request com novo token
        // eslint-disable-next-line no-param-reassign
        error.config.headers.Authorization = `Bearer ${token}`;
        return api(error.config);
      } catch (refreshError) {
        isRefreshing = false;

        // Se falhou no refresh, força logout
        if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          await forceLogout('Falha ao renovar token após múltiplas tentativas');
        }

        return Promise.reject(refreshError);
      }
    }

    // Erro 422 - Dados inválidos (pode ser refresh token inválido)
    if (status === 422) {
      const isRefreshEndpoint = error.config.url.includes('/auth/refresh');

      if (isRefreshEndpoint) {
        await forceLogout('Token de sessão expirado');
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

const refreshToken = async (): Promise<string | null> => {
  try {
    const storageAuth = await getAuthStorage();

    if (!storageAuth || !storageAuth.refresh_token) {
      return null;
    }

    api.defaults.headers.common.Authorization = `Bearer ${storageAuth.refresh_token}`;

    const res = await api.post('/auth/refresh');

    if (res.status === 200 && res.data.access_token) {
      await setAuthStorage(res.data);
      const token = res.data.access_token;
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      return token;
    }

    return null;
  } catch {
    return null;
  }
};
